import { useEffect, useMemo, useRef, useState } from "react"

import allInIconUrl from "@/assets/icons/all-in-gifts-small-icon.png"
import SortGiftsArrowsIcon from "@/assets/icons/sort-gifts-arrows.svg?react"
import SortGiftsIcon from "@/assets/icons/sort-gifts-icon.svg?react"
import TonIcon from "@/assets/icons/ton-icon.svg?react"
import type { UserGift } from "@/shared/api/types"
import { useI18n } from "@/shared/i18n"
import { cn } from "@/shared/lib/utils"
import { formatTonFromNano } from "@/shared/lib/pvpRouletteMath"

const PRESET_PRICE_MAX_CHARS = 6
const SORT_STORAGE_KEY = "pvp-gift-preset-sort-v1"

type SortDir = "asc" | "desc"

function loadSortDir(): SortDir {
  try {
    return localStorage.getItem(SORT_STORAGE_KEY) === "desc" ? "desc" : "asc"
  } catch {
    return "asc"
  }
}

function saveSortDir(dir: SortDir) {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, dir)
  } catch {
    // ignore storage errors
  }
}

function formatPresetPrice(nano: number | null | undefined): string {
  const formatted = formatTonFromNano(nano)
  if (formatted.length <= PRESET_PRICE_MAX_CHARS) return formatted
  return formatted.slice(0, PRESET_PRICE_MAX_CHARS - 1) + "…"
}

interface GiftPresetRowProps {
  gifts: UserGift[]
  disabled?: boolean
  onPlace: (giftIds: string[]) => void
}

export function GiftPresetRow({
  gifts,
  disabled = false,
  onPlace,
}: GiftPresetRowProps) {
  const { t } = useI18n()
  const [sortDir, setSortDir] = useState<SortDir>(() => loadSortDir())
  const scrollRef = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  const sortedGifts = useMemo(() => {
    const arr = gifts
      .slice()
      .sort(
        (a, b) =>
          (a.assessed_price_nano ?? 0) - (b.assessed_price_nano ?? 0)
      )
    return sortDir === "desc" ? arr.reverse() : arr
  }, [gifts, sortDir])

  const totalNano = useMemo(
    () => gifts.reduce((sum, g) => sum + (g.assessed_price_nano ?? 0), 0),
    [gifts]
  )

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => setOverflows(el.scrollWidth > el.clientWidth + 1)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [gifts.length])

  if (gifts.length === 0) return null

  const showAllIn = gifts.length > 1
  const showSort = overflows

  const toggleSort = () => {
    setSortDir((prev) => {
      const next: SortDir = prev === "asc" ? "desc" : "asc"
      saveSortDir(next)
      return next
    })
  }

  return (
    <div
      ref={scrollRef}
      className="-webkit-overflow-scrolling-touch -my-0.5 mx-[calc(50%-50vw)] flex h-11 items-center gap-1.5 overflow-x-auto px-[calc(50vw-50%)] [&::-webkit-scrollbar]:hidden"
      style={{ scrollbarWidth: "none" }}
    >
      {showSort && (
        <SortButton dir={sortDir} disabled={disabled} onClick={toggleSort} />
      )}
      {showAllIn && (
        <GiftPresetCard
          backgroundImage={allInIconUrl}
          ariaLabel={t("giftPicker.allIn")}
          price={formatPresetPrice(totalNano)}
          disabled={disabled}
          onClick={() => onPlace(gifts.map((g) => g.id))}
        />
      )}
      {sortedGifts.map((gift) => (
        <GiftPresetCard
          key={gift.id}
          backgroundImage={gift.small_image_url}
          ariaLabel={gift.collection || gift.slug}
          price={formatPresetPrice(gift.assessed_price_nano)}
          disabled={disabled}
          onClick={() => onPlace([gift.id])}
        />
      ))}
    </div>
  )
}

function SortButton({
  dir,
  disabled,
  onClick,
}: {
  dir: SortDir
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="sort gifts"
      className={cn(
        "relative size-11 shrink-0",
        disabled
          ? "cursor-not-allowed opacity-50"
          : "transition-transform active:scale-[0.97]"
      )}
    >
      <SortGiftsIcon className="size-11" aria-hidden />
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center transition-transform duration-200",
          dir === "desc" && "rotate-180"
        )}
      >
        <SortGiftsArrowsIcon className="size-5" />
      </span>
    </button>
  )
}

function GiftPresetCard({
  backgroundImage,
  ariaLabel,
  price,
  disabled,
  onClick,
}: {
  backgroundImage: string
  ariaLabel: string
  price: string
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "relative size-11 shrink-0",
        disabled
          ? "cursor-not-allowed opacity-50"
          : "transition-transform active:scale-[0.97]"
      )}
    >
      <span
        className="block size-full overflow-hidden rounded-[10px] border-2 border-[#9594B3] bg-secondary-main bg-cover bg-center"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      <span className="pointer-events-none absolute bottom-0 left-1/2 flex -translate-x-1/2 items-center gap-0.5 rounded-full bg-white py-0.5 pl-1 pr-0.5">
        <span className="text-[10px] font-semibold leading-3 tracking-[-0.3px] text-black-100">
          {price}
        </span>
        <TonIcon className="size-2.5 text-black-100" />
      </span>
    </button>
  )
}
